import { Component, KeyEvent, KeyModifiers, Tree } from "@mcleod/components";
import { ComponentUtil } from "@mcleod/components/src/base/ComponentUtil";
import { ArrayUtil, DOMUtil, Keys, ShiftedNumbers } from "@mcleod/core";
import { MainMenu } from "./MainMenu";
import { MainMenuTree } from "./MainMenuTree";
import { MenuTreeNode } from "./MenuTreeNode";
import { MenuTreeNodeLeaf } from "./MenuTreeNodeLeaf";

export class MainMenuKeyHandler {
    private mainMenu: MainMenu;

    constructor(mainMenu: MainMenu) {
        this.mainMenu = mainMenu;
        const { treeMenu, treeFavoritesMenu, closeButton } = this.mainMenu;

        if (treeMenu != null) {
            treeMenu.adHocKeyHandler = this.treeMenuKeyHandler;
        }

        if (treeFavoritesMenu != null) {
            treeFavoritesMenu.adHocKeyHandler = this.treeFavoritesKeyHandler;
        }
    }

    handleSharedKeys(event: KeyEvent) {
        if (event.defaultPrevented)
            return;
        event.shouldAutomaticallyStopPropagation = false;

        if (this.handleTabKeyEvent(event)) {
            event.consume();
            return;
        }

        if (this.handleShowFavoriteKey(event)) {
            this.consumeEvent(event);
        } else if (event.isKey(Keys.ESCAPE, {})) {
            this.consumeEvent(event);
            this.mainMenu.hide();
        } else if (event.key === Keys.ENTER) {
            const selectedMenuItem = this.activeMenuTree?.selectedNode?.menuItem;
            if (selectedMenuItem?.path != null) {
                this.consumeEvent(event);
                this.mainMenu.menuLauncher.openPage(selectedMenuItem, event.ctrlKey);
            }
        } else if (event.isKey(Keys.ARROW_UP, {})) {
            this.handleUpArrowKey(event);
        } else if (event.isKey(Keys.ARROW_DOWN, {})) {
            this.handleDownArrowKey(event);
        }
    }


    private handleTabKeyEvent(event: KeyEvent): boolean {
        if (!event.isKey(Keys.TAB, {}) && !event.isKey(Keys.TAB, {shiftKey: true})) {
            return false;
        }
        const activeElement = document.activeElement;
        const focusOrder = this.getFocusNavigationOrder();
        const firstFocusableComponent = ArrayUtil.getFirstElement(focusOrder);
        const lastFocusableComponent = ArrayUtil.getLastElement(focusOrder);
        if (event.shiftKey) {
            // if first element is focused, cycle to the last element
            if (DOMUtil.isOrContains(firstFocusableComponent._element, activeElement)) {
                lastFocusableComponent.focus();
                return true;
            }
        } else {
            // if last element is focused, cycle back to the first element
            if (DOMUtil.isOrContains(lastFocusableComponent._element, activeElement)) {
                firstFocusableComponent.focus();
                return true;
            }
        }
        return false;
    }

    private handleShowFavoriteKey(event: KeyEvent): boolean {
        return KeyModifiers.hasModifiers({ ctrlKey: false, shiftKey: true, altKey: false }, event) === true &&
        ShiftedNumbers[event.key] != null &&
        this.mainMenu.showFavorite(event.domEvent, ShiftedNumbers[event.key]) === true;
    }

    private handleUpArrowKey(event: KeyEvent) {
        const sourceComponent = this.extractComponentFromEvent(event);
        if (sourceComponent instanceof MainMenuTree && !sourceComponent.getRootNode().isFirstChildSelected()) {
            return;
        }
        const focusOrder = this.getFocusNavigationOrder();
        const currentIndex = focusOrder.indexOf(sourceComponent);
        if (currentIndex === -1) {
            return;
        }

        let previousComponent: Component;
        if (currentIndex > 0) {
            previousComponent = focusOrder[currentIndex - 1];
        } else {
            // Wrap to last
            previousComponent = ArrayUtil.getLastElement(focusOrder);
        }

        if (previousComponent == null) {
            return;
        }

        this.consumeEvent(event, sourceComponent === this.mainMenu.textSearchMenu);
        if (previousComponent instanceof MainMenuTree) {
            previousComponent.selectLastMountedNode();
        } else {
            previousComponent.focus();
        }
    }

    private handleDownArrowKey(event: KeyEvent) {
        const sourceComponent = this.extractComponentFromEvent(event);
        if (sourceComponent instanceof MainMenuTree && !sourceComponent.isLastMountedNodeSelected()) {
            return;
        }
        const focusOrder = this.getFocusNavigationOrder();
        const currentIndex = focusOrder.indexOf(sourceComponent);
        if (currentIndex === -1) {
            return;
        }

        let nextComponent: Component;
        if (currentIndex < focusOrder.length - 1) {
            nextComponent = focusOrder[currentIndex + 1];
        } else {
            // wrap around to the first component
            nextComponent = focusOrder[0];
        }

        if (nextComponent == null) {
            return;
        }

        this.consumeEvent(event, sourceComponent === this.mainMenu.textSearchMenu);
        if (nextComponent instanceof MainMenuTree) {
            nextComponent.getRootNode().selectFirstChild();
        } else {
            nextComponent.focus();
        }
    }

    private extractComponentFromEvent(event: KeyEvent): Component {
        const result = event.target as Component;
        if (result != null) {
            return result;
        }
        const domEventTarget = event.domEvent.target;
        if (domEventTarget instanceof HTMLElement) {
            return ComponentUtil.getComponentForElement(domEventTarget);
        }
        return null;
    }

    private treeMenuKeyHandler = (event: KeyEvent) => {
        const treeMenu = event.target as MainMenuTree;
        // SHIFT + F key pressed with a selected node, add or remove the selected item from the favorites tree
        if (event.isKey(Keys.F, {shiftKey: true}) && treeMenu.selectedNode instanceof MenuTreeNodeLeaf) {
            this.consumeEvent(event);
            const currentSelection = treeMenu.selectedNode;
            treeMenu.selectedNode.toggleFavorite().then(() => {
                // the tree may lose focus if the user is prompted when deleting a favorite
                treeMenu.selectedNode = currentSelection;
            });
            return;
        }

        this.handleSharedKeys(event);
    }

    private treeFavoritesKeyHandler = (event: KeyEvent) => {
        const {treeFavoritesMenu, textSearchMenu} = this.mainMenu;
        const root = treeFavoritesMenu.getRootNode();
        const selectedLeaf = treeFavoritesMenu.selectedNode;

        // Delete the selected favorite
        if (event.isKey(Keys.DELETE, {}) && selectedLeaf instanceof MenuTreeNodeLeaf) {
            this.consumeEvent(event);
            const selectedNodeIndex = root.indexOfChild(selectedLeaf);
            selectedLeaf.removeFavorite().then(() => {
                const newCount = root.getChildCount();
                if (newCount === 0) {
                    textSearchMenu.focus();
                } else {
                    const targetIndex = Math.min(selectedNodeIndex, newCount - 1);
                    root.selectChild(targetIndex);
                    treeFavoritesMenu.focus();
                }
            });
            return;
        }

        this.handleSharedKeys(event);
    }

    private get activeMenuTree(): Tree<MenuTreeNode> {
        if (this.mainMenu.treeMenu.selectedNode != null)
            return this.mainMenu.treeMenu;
        else if (this.mainMenu.treeFavoritesMenu.selectedNode != null)
            return this.mainMenu.treeFavoritesMenu;
        return null;
    }

    private getFocusNavigationOrder(): Component[] {
        const { closeButton, panelToolbox, treeFavoritesMenu, textSearchMenu, treeMenu } = this.mainMenu;
        const result: Component[] = [];
        result.push(closeButton);
        if (this.mainMenu.panelToolbox != null) {
            result.push(panelToolbox.firstToolboxButton());
            result.push(panelToolbox.lastToolboxButton());
        }
        if (treeFavoritesMenu.getRootNode().getChildCount() > 0) {
            result.push(treeFavoritesMenu);
        }
        result.push(textSearchMenu);
        if (treeMenu.getRootNode().getChildCount() > 0) {
            result.push(treeMenu);
        }
        return result;
    }

    private consumeEvent(event: KeyEvent, blurTextSearchMenu: boolean = false): void {
        // KeyHandlerGroup.handleKey() will call event.consume() if the shouldAutomaticallyStopPropagation flag is true.
        event.preventDefault();
        event.shouldAutomaticallyStopPropagation = true;
        if (blurTextSearchMenu) {
            this.mainMenu.textSearchMenu._input.blur();
        }
    }
}
